import React, { useState, useMemo } from 'react';
import { MdArrowBack, MdHomeWork } from 'react-icons/md';

// Mirrors the kit MortgagesScreen mobile variant: black hero, calculator
// card, three product tiles. Calculator uses the standard amortization
// formula — no backend call.
const PRODUCTS = [
  {
    title: 'Fixed Rate Mortgage',
    rate: '5.99%',
    description: 'Lock in your rate so payments stay predictable for the term.',
    features: [
      'Rate guaranteed for the full term',
      'Predictable monthly payments',
      'Choose 1–10 year terms',
      'Available with prepayment privileges',
    ],
    cta: 'Apply Now',
  },
  {
    title: 'Variable Rate Mortgage',
    rate: '5.45%',
    description: 'Take advantage of market fluctuations with a competitive variable rate.',
    features: [
      'Rate moves with TD Prime',
      'Lower starting rate than fixed',
      'Convert to fixed anytime',
      'Flexible payment options',
    ],
    cta: 'Learn More',
  },
  {
    title: 'First-Time Home Buyer',
    description: 'A program designed to help you purchase your first home with confidence.',
    features: [
      'Down payment as low as 5%',
      'First-time buyer incentives',
      'Extended amortization options',
      'Dedicated mortgage specialist',
    ],
    cta: 'Get Started',
  },
];

const fmtCad = (v) =>
  `$${Math.round(v).toLocaleString('en-CA')}`;

// Standard amortization: M = P · (r(1+r)^n) / ((1+r)^n − 1)
function calcMonthlyPayment({ price, down, ratePct, years }) {
  const principal = Math.max(0, (Number(price) || 0) - (Number(down) || 0));
  const r = (Number(ratePct) / 100) / 12;
  const n = (Number(years) || 0) * 12;
  if (!principal || !r || !n) return 0;
  const m = principal * (r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1);
  return Number.isFinite(m) ? m : 0;
}

const MortgagesScreen = ({ onNavigate, onBack }) => {
  const [price, setPrice]   = useState('500000');
  const [down,  setDown]    = useState('100000');
  const [years, setYears]   = useState('25');
  const [rate,  setRate]    = useState('5.99');

  const monthly = useMemo(
    () => calcMonthlyPayment({ price, down, ratePct: rate, years }),
    [price, down, rate, years],
  );

  return (
    <div className="screen bh-flow">
      <header className="bh-flow-hero dark">
        <div className="bh-flow-hero-left">
          <button className="bh-flow-back" onClick={onBack || (() => onNavigate('services'))} aria-label="Back to services">
            <MdArrowBack />
          </button>
          <h1 className="bh-flow-title">Mortgages</h1>
        </div>
        <MdHomeWork size={22} style={{ color: 'rgba(255,255,255,0.7)' }} />
      </header>

      <div className="bh-flow-body">
        {/* Calculator */}
        <div className="bh-form-card">
          <h2 className="bh-form-card-title">Mortgage Calculator</h2>

          <div className="bh-field">
            <label htmlFor="mort-price" className="bh-field-label">Home Price</label>
            <input
              id="mort-price"
              className="bh-field-input"
              type="number"
              min="0"
              value={price}
              onChange={e => setPrice(e.target.value)}
            />
          </div>
          <div className="bh-field">
            <label htmlFor="mort-down" className="bh-field-label">Down Payment</label>
            <input
              id="mort-down"
              className="bh-field-input"
              type="number"
              min="0"
              value={down}
              onChange={e => setDown(e.target.value)}
            />
          </div>
          <div style={{ display: 'flex', gap: 'var(--space-2xs)' }}>
            <div className="bh-field" style={{ flex: 1 }}>
              <label htmlFor="mort-years" className="bh-field-label">Term (years)</label>
              <input
                id="mort-years"
                className="bh-field-input"
                type="number"
                min="1"
                max="35"
                value={years}
                onChange={e => setYears(e.target.value)}
              />
            </div>
            <div className="bh-field" style={{ flex: 1 }}>
              <label htmlFor="mort-rate" className="bh-field-label">Rate (%)</label>
              <input
                id="mort-rate"
                className="bh-field-input"
                type="number"
                min="0"
                step="0.01"
                value={rate}
                onChange={e => setRate(e.target.value)}
              />
            </div>
          </div>

          <div
            style={{
              marginTop: 'var(--space-2xs)',
              padding: 'var(--space-md)',
              background: 'linear-gradient(135deg, var(--td-green) 0%, var(--td-dark-green) 100%)',
              color: 'var(--bh-black)',
              textAlign: 'center',
              borderRadius: 'var(--radius-md)',
            }}
          >
            <p style={{ margin: 0, font: 'var(--bh-body-sm)', opacity: 0.85 }}>
              Estimated monthly payment
            </p>
            <p style={{ margin: 'var(--space-3xs) 0 0', font: 'var(--bh-display)', fontWeight: 800, letterSpacing: '-0.02em' }}>
              {fmtCad(monthly)}
            </p>
            <p style={{ margin: 'var(--space-3xs) 0 0', font: 'var(--bh-caption)', opacity: 0.85 }}>
              at {rate || 0}% APR over {years || 0} years
            </p>
          </div>
        </div>

        {/* Product cards */}
        {PRODUCTS.map(p => (
          <article key={p.title} className="bh-product-card">
            <h3 className="bh-product-card-title">{p.title}</h3>
            {p.rate && <p className="bh-product-card-rate">{p.rate}</p>}
            <p className="bh-product-card-desc">{p.description}</p>
            <ul className="bh-product-card-features">
              {p.features.map(f => <li key={f}>{f}</li>)}
            </ul>
            <button className="bh-btn-cta">{p.cta}</button>
          </article>
        ))}
      </div>
    </div>
  );
};

export default MortgagesScreen;
